import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import "./date-input.scss";
import CalendarIcon from "../../SVG/calendar-icon";
import WarningIcon from "../../SVG/warning-icon";
import moment from "moment";

interface OnBlurData {
  name: string;
  value: string;
  required: boolean;
}

interface DateInputProps {
  id: string;
  label: string;
  defaultValue?: string;
  hint?: string;
  error?: string;
  hasError?: boolean;
  required?: boolean;
  onBlur?: (data: OnBlurData) => void;
  onChange?: (data: OnBlurData) => void;
  readOnly?: boolean;
  value?: string;
  format?: string;
  className?: string;
}

/**
 * Parse a date string using moment.js
 * Attempts several formats if the expected format fails
 */
const parseDateString = (
  dateString: string,
  expectedFormat: string
): moment.Moment | null => {
  if (!dateString) return null;

  // Try to parse with the expected format
  let dateMoment = moment(dateString, expectedFormat, true);

  // If valid, return it
  if (dateMoment.isValid()) {
    return dateMoment;
  }

  // Try ISO format (YYYY-MM-DD)
  dateMoment = moment(dateString, "YYYY-MM-DD", true);
  if (dateMoment.isValid()) {
    return dateMoment;
  }

  // Try common formats
  const commonFormats = [
    "MM/DD/YYYY", // US
    "DD.MM.YYYY", // European
    "DD/MM/YYYY", // UK
    "YYYY/MM/DD", // Another ISO-like
    "D MMM YYYY", // 15 Mar 2023
    "D MMMM YYYY", // 15 March 2023
    "MMM D, YYYY", // Mar 15, 2023
    "MMMM D, YYYY", // March 15, 2023
  ];

  for (const format of commonFormats) {
    dateMoment = moment(dateString, format, true);
    if (dateMoment.isValid()) {
      console.log(
        `Successfully parsed "${dateString}" with format "${format}"`
      );
      return dateMoment;
    }
  }

  // Last chance: try to parse without strict mode (more permissive)
  dateMoment = moment(dateString);
  if (dateMoment.isValid()) {
    console.log(`Parsed "${dateString}" with moment's flexible parsing`);
    return dateMoment;
  }

  console.log(`All parsing methods failed for "${dateString}"`);
  return null;
};

export function DateInput(props: DateInputProps) {
  const [error, setError] = useState("");
  const [value, setValue] = useState("");
  const [displayValue, setDisplayValue] = useState("");
  const [hasError, setHasError] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);

  // Fixed internal format that HTML date inputs expect
  const internalFormat = "YYYY-MM-DD"; // Moment.js uses uppercase format codes

  // Memoize the displayFormat calculation
  const displayFormat = useMemo(() => {
    if (!props.format) return internalFormat;

    // Convert date-fns format to moment format (common patterns)
    return props.format
      .replace(/yyyy/g, "YYYY")
      .replace(/yy/g, "YY")
      .replace(/MM/g, "MM")
      .replace(/dd/g, "DD")
      .replace(/d/g, "D")
      .replace(/EEEE/g, "dddd")
      .replace(/EEE/g, "ddd")
      .replace(/do/g, "Do");
  }, [props.format, internalFormat]);

  // Memoize the isCustomFormat calculation to avoid recalculating on every render
  const isCustomFormat = useMemo(() => {
    return displayFormat !== internalFormat;
  }, [displayFormat, internalFormat]);

  // Format for display with useCallback
  const formatForDisplay = useCallback(
    (dateValue: string): string => {
      if (!dateValue) return "";

      try {
        // Parse date using the internal format (or try various formats)
        const dateMoment = parseDateString(dateValue, internalFormat);

        if (!dateMoment || !dateMoment.isValid()) {
          console.error("Could not parse date:", dateValue);
          return dateValue;
        }

        // Format using the display format
        return dateMoment.format(displayFormat);
      } catch (error) {
        console.error("Date formatting error:", error);
        return dateValue;
      }
    },
    [displayFormat, internalFormat]
  );

  // Update values when props change
  useEffect(() => {
    // Force re-render of display value when format prop changes
    if (value) {
      const formattedValue = formatForDisplay(value);
      setDisplayValue(formattedValue);
      console.log(
        "Format changed, updating display:",
        value,
        "->",
        formattedValue
      );
    }
  }, [props.format]);

  // Try formatting a test date to verify the format works
  useEffect(() => {
    if (props.format && props.format !== internalFormat) {
      // Test with a known good date
      const testDate = "2023-05-15"; // May 15, 2023
      const testFormatted = formatForDisplay(testDate);
      console.log(
        `Test date formatting with format "${displayFormat}":`,
        testDate,
        "->",
        testFormatted
      );
    }
  }, [props.format]);

  useEffect(() => {
    if (props.value) {
      setValue(props.value);
      const formattedValue = formatForDisplay(props.value);
      setDisplayValue(formattedValue);
      console.log(
        "Setting from value prop:",
        props.value,
        "->",
        formattedValue
      );
    } else if (props.defaultValue) {
      setValue(props.defaultValue);
      const formattedValue = formatForDisplay(props.defaultValue);
      setDisplayValue(formattedValue);
      console.log(
        "Setting from defaultValue prop:",
        props.defaultValue,
        "->",
        formattedValue
      );
    }
  }, [props.value, props.defaultValue, props.format]);

  useEffect(() => {
    setError(props.error || "");
    setHasError(props.hasError || false);
  }, [props.error, props.hasError]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const internalValue = event.target.value;
      setValue(internalValue);

      const formattedValue = formatForDisplay(internalValue);
      setDisplayValue(formattedValue);
      console.log("handleChange:", internalValue, "->", formattedValue);

      const data: OnBlurData = {
        name: event.target.name,
        value: internalValue, // Always send the internal value to callbacks
        required: props.required ?? false,
      };

      if (props.onChange) {
        props.onChange(data);
      }
    },
    [formatForDisplay, props.onChange, props.required]
  );

  const handleBlurEvent = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const internalValue = event.target.value;
      setValue(internalValue);

      const formattedValue = formatForDisplay(internalValue);
      setDisplayValue(formattedValue);
      console.log("handleBlur:", internalValue, "->", formattedValue);

      const data: OnBlurData = {
        name: event.target.name,
        value: internalValue, // Always send the internal value to callbacks
        required: props.required ?? false,
      };

      if (props.onBlur) {
        props.onBlur(data);
      }
    },
    [formatForDisplay, props.onBlur, props.required]
  );

  const handleDisplayClick = useCallback(() => {
    if (!props.readOnly && dateInputRef.current) {
      try {
        // Try using showPicker() API (newer browsers)
        dateInputRef.current.showPicker();
      } catch (error) {
        // Fallback for browsers that don't support showPicker
        dateInputRef.current.focus();

        // Create a synthetic click event
        const clickEvent = new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: true,
        });

        // Trigger click on the date input to open native date picker
        dateInputRef.current.dispatchEvent(clickEvent);
      }
    }
  }, [props.readOnly]);

  // For debugging
  console.log("Render with:", {
    value,
    displayValue,
    isCustomFormat,
    originalFormat: props.format,
    momentFormat: displayFormat,
  });

  return (
    <>
      <div className={`custom-date-input-container ${props.className}`}>
        <div className="date-input-container">
          <div className="label">
            {props.label}
            {props.required && <span className="required"> *</span>}
          </div>
          <div
            className={error || hasError ? "date-input error" : "date-input"}
          >
            {isCustomFormat ? (
              <div className="custom-format-wrapper">
                <input
                  type="text"
                  id={`${props.id}-display`}
                  name={`${props.id}-display`}
                  value={displayValue}
                  readOnly={true}
                  className="formatted-display"
                  onClick={handleDisplayClick}
                />
                <input
                  type="date"
                  id={props.id}
                  name={props.id}
                  ref={dateInputRef}
                  value={value}
                  onChange={handleChange}
                  onBlur={handleBlurEvent}
                  readOnly={props.readOnly}
                  className="hidden-date-input"
                />
              </div>
            ) : (
              <input
                type="date"
                id={props.id}
                name={props.id}
                onBlur={handleBlurEvent}
                value={value}
                onChange={handleChange}
                readOnly={props.readOnly}
              />
            )}
            {!props.readOnly && (
              <span className="date-icon" onClick={handleDisplayClick}>
                <CalendarIcon />
              </span>
            )}
            {props.hasError && (
              <span className="date-icon">
                <WarningIcon />
              </span>
            )}
          </div>
          <div className="additional-info">
            {props.hint && <div className="hint-text">{props.hint}</div>}
            {error && <div className="error-message">{error}</div>}
          </div>
        </div>
      </div>
    </>
  );
}
